import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Copy, Save, Search } from "lucide-react";
import moment from "moment";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import useDebounced from "../../../utils/useDebounced";

// Week-by-week roster: agents down the side, Monday to Sunday across. Edits
// are held locally and saved in one go, so planning a week is a few clicks and
// one Save rather than a request per cell.
//
// A cell is either a shift, a week-off, or empty -- empty means the agent's
// default shift from their profile applies, shown greyed out.

const PAGE_SIZE = 20;
const cellKey = (agentId, date) => `${agentId}|${date}`;
const shiftText = (start, end) => `${start} – ${end}`;

const confirmDiscard = (dirty) =>
  !dirty || window.confirm("You have unsaved roster changes. Discard them?");

export default function WeeklyRoster() {
  const [weekStart, setWeekStart] = useState(""); // "" = this week (server decides)
  const [week, setWeek] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  // { "agentId|date": { type: "shift"|"off"|"default", shiftStart, shiftEnd } }
  const [edits, setEdits] = useState({});
  const dirty = Object.keys(edits).length > 0;

  const [editor, setEditor] = useState(null); // { anchor, row, date }
  const [draft, setDraft] = useState({ type: "shift", shiftStart: "09:00", shiftEnd: "18:00" });
  const [copyOpen, setCopyOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceRoster, {
        params: {
          weekStart: weekStart || undefined,
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
        },
      });
      setWeek(data?.week || null);
      setRows(data?.data || []);
      setPages(data?.pagination?.pages || 1);
      setTotal(data?.pagination?.total || 0);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the roster");
    } finally {
      setLoading(false);
    }
  }, [weekStart, page, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  /** What a cell shows right now: the unsaved edit if any, else the saved entry. */
  const cellValue = (row, date) => {
    const edit = edits[cellKey(row.agent.id, date)];
    if (edit) return edit.type === "default" ? null : edit;
    return row.days[date];
  };

  // Shifts already used on this page, offered as one-click picks in the editor.
  const quickShifts = useMemo(() => {
    const seen = new Map();
    const add = (s, e) => s && e && seen.set(shiftText(s, e), { shiftStart: s, shiftEnd: e });
    rows.forEach((r) => {
      add(r.agent.shiftStart, r.agent.shiftEnd);
      Object.values(r.days).forEach((d) => d?.type === "shift" && add(d.shiftStart, d.shiftEnd));
    });
    Object.values(edits).forEach((d) => d.type === "shift" && add(d.shiftStart, d.shiftEnd));
    return [...seen.values()].slice(0, 6);
  }, [rows, edits]);

  const go = (offsetWeeks) => {
    if (!week || !confirmDiscard(dirty)) return;
    setEdits({});
    setPage(1);
    setWeekStart(
      offsetWeeks === 0 ? "" : moment(week.start).add(offsetWeeks, "weeks").format("YYYY-MM-DD")
    );
  };

  const openEditor = (event, row, date) => {
    const current = cellValue(row, date);
    setDraft(
      current
        ? {
            type: current.type,
            shiftStart: current.shiftStart || row.agent.shiftStart || "09:00",
            shiftEnd: current.shiftEnd || row.agent.shiftEnd || "18:00",
          }
        : {
            type: "default",
            shiftStart: row.agent.shiftStart || "09:00",
            shiftEnd: row.agent.shiftEnd || "18:00",
          }
    );
    setEditor({ anchor: event.currentTarget, row, date });
  };

  const draftProblem =
    draft.type === "shift" &&
    (!draft.shiftStart || !draft.shiftEnd
      ? "Set both times"
      : draft.shiftStart === draft.shiftEnd
      ? "Start and end cannot match"
      : "");

  const applyDraft = (dates) => {
    if (!editor || draftProblem) return;
    const value =
      draft.type === "shift"
        ? { type: "shift", shiftStart: draft.shiftStart, shiftEnd: draft.shiftEnd }
        : { type: draft.type };
    setEdits((prev) => {
      const next = { ...prev };
      dates.forEach((d) => {
        const key = cellKey(editor.row.agent.id, d);
        const saved = editor.row.days[d];
        // Setting a cell back to what is already saved is not a change.
        const same =
          (value.type === "default" && !saved) ||
          (saved &&
            saved.type === value.type &&
            (value.type === "off" ||
              (saved.shiftStart === value.shiftStart && saved.shiftEnd === value.shiftEnd)));
        if (same) delete next[key];
        else next[key] = value;
      });
      return next;
    });
    setEditor(null);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const entries = Object.entries(edits).map(([key, v]) => {
        const [agentId, date] = key.split("|");
        return { agentId, date, ...v };
      });
      const { data } = await axiosInstance.put(apiEndPointsConfig.attendanceRoster, { entries });
      setNotice(data?.message || "Roster saved");
      setEdits({});
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the roster");
    } finally {
      setSaving(false);
    }
  };

  const copyLastWeek = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axiosInstance.post(`${apiEndPointsConfig.attendanceRoster}/copy`, {
        fromWeekStart: moment(week.start).subtract(1, "week").format("YYYY-MM-DD"),
        toWeekStart: week.start,
      });
      setNotice(data?.message || "Roster copied");
      setEdits({});
      setCopyOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not copy the roster");
      setCopyOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const renderCell = (row, date) => {
    const value = cellValue(row, date);
    const changed = Boolean(edits[cellKey(row.agent.id, date)]);
    let content;
    if (value?.type === "off") {
      content = (
        <Chip label="Week Off" size="small" sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 600 }} />
      );
    } else if (value?.type === "shift") {
      content = (
        <Chip
          label={shiftText(value.shiftStart, value.shiftEnd)}
          size="small"
          sx={{ bgcolor: "#eef2ff", color: "#0C237B", fontWeight: 600 }}
        />
      );
    } else if (row.agent.shiftStart) {
      content = (
        <Tooltip title="Default shift from the agent's profile">
          <Typography variant="caption" sx={{ color: "#9ca3af", fontStyle: "italic" }}>
            {shiftText(row.agent.shiftStart, row.agent.shiftEnd || "?")}
          </Typography>
        </Tooltip>
      );
    } else {
      content = (
        <Typography variant="caption" sx={{ color: "#d1d5db" }}>
          —
        </Typography>
      );
    }

    return (
      <TableCell
        key={date}
        align="center"
        onClick={(e) => openEditor(e, row, date)}
        sx={{
          cursor: "pointer",
          bgcolor: changed ? "#fffbeb" : date === week?.today ? "#f8fafc" : undefined,
          borderLeft: "1px solid #f3f4f6",
          "&:hover": { bgcolor: "#f1f5f9" },
          px: 1,
        }}
      >
        {content}
      </TableCell>
    );
  };

  const weekLabel = week
    ? `${moment(week.start).format("D MMM")} – ${moment(week.end).format("D MMM YYYY")}`
    : "";

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <IconButton size="small" onClick={() => go(-1)} disabled={!week}>
          <ChevronLeft size={18} />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: "#111827", minWidth: 170, textAlign: "center" }}>
          {weekLabel}
        </Typography>
        <IconButton size="small" onClick={() => go(1)} disabled={!week}>
          <ChevronRight size={18} />
        </IconButton>
        <Button size="small" onClick={() => go(0)} sx={{ textTransform: "none", color: "#0C237B" }}>
          This week
        </Button>

        <TextField
          size="small"
          placeholder="Search agents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 220 }, ml: { sm: 1 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#9ca3af" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ flex: 1 }} />

        <Button
          variant="outlined"
          startIcon={<Copy size={16} />}
          disabled={!week || saving}
          onClick={() => setCopyOpen(true)}
          sx={{ textTransform: "none", borderRadius: "8px", borderColor: "#0C237B", color: "#0C237B" }}
        >
          Copy last week
        </Button>
        {dirty && (
          <Button onClick={() => setEdits({})} disabled={saving} sx={{ textTransform: "none", color: "#6b7280" }}>
            Discard
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Save size={16} />}
          disabled={!dirty || saving}
          onClick={save}
          sx={{ textTransform: "none", bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px" }}
        >
          {saving ? "Saving..." : dirty ? `Save (${Object.keys(edits).length})` : "Save"}
        </Button>
      </Paper>

      <Paper sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 960 }}>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                  <TableCell sx={{ minWidth: 180 }}>Agent</TableCell>
                  {week?.days.map((d) => (
                    <TableCell
                      key={d}
                      align="center"
                      sx={{ color: d === week.today ? "#0C237B !important" : undefined, minWidth: 110 }}
                    >
                      <div>{moment(d).format("ddd")}</div>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {moment(d).format("D MMM")}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#9ca3af" }}>
                      No active agents match.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.agent.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.agent.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                        {row.agent.userid}
                        {row.agent.team ? ` · ${row.agent.team}` : ""}
                      </Typography>
                    </TableCell>
                    {week?.days.map((d) => renderCell(row, d))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {total > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 2 }}>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total} · click a day to
            change it
          </Typography>
          {pages > 1 && (
            <Pagination
              count={pages}
              page={page}
              onChange={(e, v) => {
                if (!confirmDiscard(dirty)) return;
                setEdits({});
                setPage(v);
              }}
              size="small"
              shape="rounded"
            />
          )}
        </Box>
      )}

      {/* ── Cell editor ─────────────────────────────────────────────────── */}
      <Popover
        open={Boolean(editor)}
        anchorEl={editor?.anchor}
        onClose={() => setEditor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {editor && (
          <Box sx={{ p: 2, width: 300 }}>
            <Typography sx={{ fontWeight: 700 }}>{editor.row.agent.name}</Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              {moment(editor.date).format("dddd, D MMM YYYY")}
            </Typography>

            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={draft.type}
              onChange={(e, v) => v && setDraft((d) => ({ ...d, type: v }))}
              sx={{ mt: 1.5, "& .MuiToggleButton-root": { textTransform: "none" } }}
            >
              <ToggleButton value="shift">Shift</ToggleButton>
              <ToggleButton value="off">Week Off</ToggleButton>
              <ToggleButton value="default">Default</ToggleButton>
            </ToggleButtonGroup>

            {draft.type === "shift" && (
              <>
                <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                  <TextField
                    label="Start"
                    type="time"
                    size="small"
                    fullWidth
                    value={draft.shiftStart}
                    onChange={(e) => setDraft((d) => ({ ...d, shiftStart: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End"
                    type="time"
                    size="small"
                    fullWidth
                    value={draft.shiftEnd}
                    onChange={(e) => setDraft((d) => ({ ...d, shiftEnd: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                {quickShifts.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
                    {quickShifts.map((q) => (
                      <Chip
                        key={shiftText(q.shiftStart, q.shiftEnd)}
                        label={shiftText(q.shiftStart, q.shiftEnd)}
                        size="small"
                        variant="outlined"
                        onClick={() => setDraft((d) => ({ ...d, ...q }))}
                      />
                    ))}
                  </Box>
                )}
                {draftProblem && (
                  <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mt: 0.5 }}>
                    {draftProblem}
                  </Typography>
                )}
              </>
            )}
            {draft.type === "default" && (
              <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mt: 1 }}>
                {editor.row.agent.shiftStart
                  ? `Uses the profile shift: ${shiftText(editor.row.agent.shiftStart, editor.row.agent.shiftEnd || "?")}`
                  : "No profile shift is set, so the day has no planned start."}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="contained"
                disabled={Boolean(draftProblem)}
                onClick={() => applyDraft([editor.date])}
                sx={{ textTransform: "none", bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, flex: 1 }}
              >
                Apply
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={Boolean(draftProblem)}
                onClick={() => applyDraft(week.days)}
                sx={{ textTransform: "none", borderColor: "#0C237B", color: "#0C237B", flex: 1 }}
              >
                Whole week
              </Button>
            </Box>
          </Box>
        )}
      </Popover>

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Copy last week?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every agent's roster from the week of{" "}
            <strong>{week && moment(week.start).subtract(1, "week").format("D MMM")}</strong> will be copied onto{" "}
            <strong>{weekLabel}</strong>. Days already planned this week are overwritten where last week had an
            entry.
            {dirty && " Your unsaved changes will be discarded."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCopyOpen(false)} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={copyLastWeek}
            sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px" }}
          >
            {saving ? "Copying..." : "Copy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
